import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ImagePlus, Send, X } from 'lucide-react';
import { DAMAGE_KEYS, vehicleDamagesApi } from '../../api/vehicleDamages';
import { fmtDate } from '../../lib/dates';
import { DAMAGE_PHOTO_MAX_BYTES, MAX_DAMAGE_PHOTOS, damagePhoto, damagePhotoRefusal } from '../../lib/damagePhoto';
import type { DamageOption } from '../../types';
import { Button, Callout, SelectField, Switch, TextAreaField, TextField, cx, useToast } from '../charte';
import { Sheet } from '../shell/Sheet';
import { DamagePhotoStrip } from './DamagePhotos';

// Signaler un dégât (24/10/2026) : le moniteur qui prend un véhicule de l'école et y repère un défaut le
// prend en photo et prévient l'auto-école. Le véhicule de sa leçon est choisi d'office ; les dégâts déjà
// connus du véhicule sont montrés d'abord, pour ne pas signaler deux fois le même.

type Photo = { id: string; blob: Blob; preview: string };

const failure = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message ? message : Array.isArray(message) ? String(message[0]) : fallback;
};

function Choices({ label, options, value, onChange }: { label: string; options: DamageOption[]; value: string; onChange: (key: string) => void }) {
  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className="mb-1.5 text-[12.5px] font-medium text-secondary">{label}</legend>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option.key === value;
          return (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.key)}
              className={cx(
                'h-9 rounded-pill border px-3.5 text-[13px] font-medium transition-colors',
                active ? 'border-primary bg-primary text-white' : 'border-line bg-white text-body hover:border-primary-200',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function DamageReportSheet({ vehicleId: initialVehicle, onClose }: { vehicleId?: string; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const options = useQuery({ queryKey: DAMAGE_KEYS.vehicles, queryFn: vehicleDamagesApi.vehicles });
  const [vehicleId, setVehicleId] = useState(initialVehicle ?? '');
  const [moment, setMoment] = useState('');
  const [area, setArea] = useState('');
  const [description, setDescription] = useState('');
  const [drivable, setDrivable] = useState(true);
  const [mileage, setMileage] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Véhicule de la leçon choisi d'office une fois la liste reçue.
  useEffect(() => {
    if (!vehicleId && options.data) setVehicleId(options.data.suggestedVehicleId ?? (options.data.vehicles.length === 1 ? options.data.vehicles[0].id : ''));
  }, [options.data, vehicleId]);
  // Aperçus locaux libérés à la fermeture.
  const previews = useRef<string[]>([]);
  useEffect(() => () => previews.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const known = useQuery({
    queryKey: DAMAGE_KEYS.known(vehicleId),
    queryFn: () => vehicleDamagesApi.known(vehicleId),
    enabled: !!vehicleId,
  });
  const vehicle = useMemo(() => options.data?.vehicles.find((v) => v.id === vehicleId), [options.data, vehicleId]);

  const send = useMutation({
    mutationFn: () =>
      vehicleDamagesApi.report({ vehicleId, moment, area, description, drivable, mileage, photos: photos.map((p) => p.blob) }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['vehicle-damages'] });
      toast('Dégât signalé : l’auto-école est prévenue.');
      onClose();
    },
    onError: (e) => setError(failure(e, 'Envoi impossible. Vérifiez votre connexion et réessayez.')),
  });

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    const room = MAX_DAMAGE_PHOTOS - photos.length;
    if (room <= 0) return setError(`${MAX_DAMAGE_PHOTOS} photos au plus par signalement.`);
    const chosen = [...files].slice(0, room);
    const refused = chosen.map(damagePhotoRefusal).find(Boolean);
    if (refused) return setError(refused);
    setPreparing(true);
    try {
      const ready: Photo[] = [];
      for (const file of chosen) {
        const blob = await damagePhoto(file);
        if (blob.size > DAMAGE_PHOTO_MAX_BYTES) {
          setError('Photo trop volumineuse (10 Mo au plus).');
          continue;
        }
        const preview = URL.createObjectURL(blob);
        previews.current.push(preview);
        ready.push({ id: `${Date.now()}-${Math.random()}`, blob, preview });
      }
      setPhotos((current) => [...current, ...ready].slice(0, MAX_DAMAGE_PHOTOS));
      if (files.length > room) setError(`${MAX_DAMAGE_PHOTOS} photos au plus par signalement.`);
    } finally {
      setPreparing(false);
    }
  };

  const submit = () => {
    if (!vehicleId) return setError('Choisissez le véhicule.');
    if (!moment) return setError('Indiquez à quel moment vous avez constaté le dégât.');
    if (!area) return setError('Indiquez la zone du véhicule concernée.');
    if (!description.trim()) return setError('Décrivez le dégât en quelques mots.');
    if (!photos.length) return setError('Ajoutez au moins une photo du dégât.');
    if (mileage.trim() && !/^\d{1,7}$/.test(mileage.trim())) return setError('Kilométrage invalide : chiffres seulement.');
    setError(null);
    send.mutate();
  };

  const busy = send.isPending || preparing;
  return (
    <Sheet
      title="Signaler un dégât"
      description="Prenez le dégât en photo : l’auto-école est prévenue et suit l’état du véhicule."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={send.isPending}>
            Annuler
          </Button>
          <Button variant="primary" icon={Send} loading={send.isPending} disabled={preparing || !options.data} className="ml-auto" onClick={submit}>
            Envoyer à l’auto-école
          </Button>
        </>
      }
    >
      {options.isLoading && <p className="py-6 text-center text-sm text-secondary">Chargement des véhicules…</p>}
      {options.isError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Véhicules indisponibles : {failure(options.error, 'réessayez plus tard.')}
        </p>
      )}
      {options.data && !options.data.vehicles.length && <Callout tone="warn">Aucun véhicule de l’auto-école n’est enregistré.</Callout>}
      {options.data && options.data.vehicles.length > 0 && (
        <div className="flex flex-col gap-4">
          <SelectField label="Véhicule" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">Choisir le véhicule…</option>
            {options.data.vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.immatriculation} · {v.brand} {v.model}
                {v.id === options.data?.suggestedVehicleId ? ' (votre leçon)' : ''}
              </option>
            ))}
          </SelectField>

          {vehicle && known.data && known.data.length > 0 && (
            <div className="rounded-xl bg-warn-soft px-3.5 py-3 ring-1 ring-warn-line/40">
              <p className="text-[13px] font-semibold text-heading">
                Déjà signalé sur ce véhicule ({known.data.length})
              </p>
              <p className="mb-2 text-xs text-secondary">Le dégât que vous voyez y figure ? Inutile de le signaler à nouveau.</p>
              <ul className="flex flex-col gap-2.5">
                {known.data.map((d) => (
                  <li key={d.id} className="flex flex-col gap-1.5">
                    <p className="text-[13px] text-body">
                      <span className="font-medium">{d.areaLabel}</span> · {d.description}
                      <span className="text-xs text-muted"> · {fmtDate(d.reportedAt)}</span>
                    </p>
                    <DamagePhotoStrip photos={d.photos} title={`${d.areaLabel} · ${fmtDate(d.reportedAt)}`} size="sm" max={4} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Choices label="Moment du constat" options={options.data.moments} value={moment} onChange={setMoment} />
          <Choices label="Zone du véhicule" options={options.data.areas} value={area} onChange={setArea} />
          <TextAreaField
            label="Description"
            maxLength={1000}
            placeholder="Rayure de 10 cm sur la portière, rétroviseur fissuré…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="flex flex-col gap-2">
            <p className="text-[12.5px] font-medium text-secondary">
              Photos ({photos.length}/{MAX_DAMAGE_PHOTOS})
            </p>
            {photos.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {photos.map((photo, index) => (
                  <li key={photo.id} className="relative h-20 w-20 overflow-hidden rounded-xl border border-line">
                    <img src={photo.preview} alt={`Photo ${index + 1}`} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      aria-label={`Retirer la photo ${index + 1}`}
                      onClick={() => setPhotos((current) => current.filter((p) => p.id !== photo.id))}
                      className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-heading/70 text-white"
                    >
                      <X size={14} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-2">
              <Button icon={Camera} loading={preparing} disabled={send.isPending || photos.length >= MAX_DAMAGE_PHOTOS} onClick={() => camera.current?.click()}>
                Prendre une photo
              </Button>
              <Button variant="ghost" icon={ImagePlus} disabled={busy || photos.length >= MAX_DAMAGE_PHOTOS} onClick={() => gallery.current?.click()}>
                Choisir dans la galerie
              </Button>
            </div>
            <input
              ref={camera}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              aria-label="Prendre une photo du dégât"
              onChange={(e) => {
                const files = e.currentTarget.files;
                void add(files).finally(() => {
                  if (camera.current) camera.current.value = '';
                });
              }}
            />
            <input
              ref={gallery}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              multiple
              hidden
              aria-label="Photos du dégât"
              onChange={(e) => {
                const files = e.currentTarget.files;
                void add(files).finally(() => {
                  if (gallery.current) gallery.current.value = '';
                });
              }}
            />
          </div>

          <Switch label="Le véhicule peut encore rouler" checked={drivable} onChange={setDrivable} />
          {!drivable && (
            <Callout tone="warn">L’auto-école est prévenue tout de suite : elle immobilise le véhicule et décale les leçons si besoin.</Callout>
          )}
          <TextField
            label="Kilométrage (facultatif)"
            inputMode="numeric"
            maxLength={7}
            placeholder="45 210"
            value={mileage}
            onChange={(e) => setMileage(e.target.value.replace(/\s/g, ''))}
          />
        </div>
      )}
      {error && (
        <p role="alert" className="mt-4 rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          {error}
        </p>
      )}
    </Sheet>
  );
}
